import {
  useCreateProduct,
  useCreateProductWithOptions,
  useMenuTree,
} from '@/features/menu/hooks/useMenu';
import { OPTIONAL_GROUP_NAME, REQUIRED_GROUP_NAME } from '@/features/menu/constants';
import { useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { compactLocalized, loc } from '@/utils/localized';
import { inputToMinor } from '@/utils/money';
import type { ProductWithOptionsCreate } from '@restaurant/api-contracts';
import { useEffect, useRef, useState } from 'react';
import {
  Block,
  BlockTitle,
  Button,
  Link as F7Link,
  List,
  ListInput,
  ListItem,
  Navbar,
  NavLeft,
  NavTitle,
  NavRight,
  Page,
  PageContent,
  Popup,
  Preloader,
  Segmented,
  f7,
} from 'framework7-react';

// Monotonic key so rows stay stable across add/remove (index keys would reorder
// inputs and drop focus). Module-level counter, never rendered.
let nextRowKey = 0;
type Row = { key: number; name: string; price: string };
const emptyRow = (): Row => ({ key: nextRowKey++, name: '', price: '' });

// A single choice row in an advanced-mode option zone (name + price delta + the
// "default selection" flag). Shares the monotonic key scheme so add/remove keeps
// input focus stable, same as the simple grid rows.
type ChoiceRow = { key: number; name: string; price: string };
const emptyChoice = (): ChoiceRow => ({ key: nextRowKey++, name: '', price: '' });

// One of the two flat option zones (Required / Optional). Each maps to exactly
// one product option; empty zones are dropped from the payload on save.
type Zone = {
  groupName: string;
  isMultiple: boolean;
  choices: ChoiceRow[];
};
const emptyZone = (groupName: string): Zone => ({
  groupName,
  isMultiple: false,
  choices: [emptyChoice()],
});

// Option-group default names moved to features/menu/constants.ts; re-exported
// here so any existing importers of this module keep working.
export { REQUIRED_GROUP_NAME, OPTIONAL_GROUP_NAME };

/**
 * Quick-add popup: pick a section, then add items in one of two modes. "Multiple
 * items" (default) types several name + optional-price rows at once. "One detailed
 * item" builds a single product with a required and an optional option group,
 * each with their own choices - created atomically via the /products/full
 * endpoint. Opened from the menu's Add-item entry points; richer per-item editing
 * (allergens, per-channel pricing) still lives in the product page.
 */
export function BulkAddProductsPopup({
  open,
  menuId,
  categoryId,
  onClose,
}: {
  open: boolean;
  menuId: string;
  categoryId?: string;
  onClose: () => void;
}) {
  const t = useT();
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const { data: tree, isLoading } = useMenuTree(menuId);
  const createProduct = useCreateProduct();
  const createProductWithOptions = useCreateProductWithOptions();

  const categories = (tree?.categories ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder);
  const [selectedCat, setSelectedCat] = useState('');
  const [mode, setMode] = useState<'simple' | 'advanced'>('simple');
  // Start with a single row; the "+" adds more when the operator wants several.
  const [rows, setRows] = useState<Row[]>(() => [emptyRow()]);
  // Advanced-mode single-product draft: base fields + the two option zones.
  const [prodName, setProdName] = useState('');
  const [prodPrice, setProdPrice] = useState('');
  const [requiredZone, setRequiredZone] = useState<Zone>(() => emptyZone(''));
  const [optionalZone, setOptionalZone] = useState<Zone>(() => emptyZone(''));
  const submitting = useRef(false);

  // The restaurant's currency symbol (e.g. €, $, ₫) shown next to each price.
  const currencySymbol = (() => {
    try {
      return (
        new Intl.NumberFormat(undefined, { style: 'currency', currency })
          .formatToParts(0)
          .find((p) => p.type === 'currency')?.value ?? currency
      );
    } catch {
      return currency;
    }
  })();

  // Reset the form each time the popup opens, seeding the section from the row it
  // was opened from (falls back to the first section).
  useEffect(() => {
    if (!open) return;
    setSelectedCat(categoryId ?? '');
    setMode('simple');
    setRows([emptyRow()]);
    setProdName('');
    setProdPrice('');
    setRequiredZone(emptyZone(''));
    setOptionalZone(emptyZone(''));
  }, [open, categoryId, t]);

  const effectiveCat = selectedCat || categories[0]?.id || '';
  const effectiveCatName = categories.find((c) => c.id === effectiveCat)?.name;

  // Section picker - same action-sheet idiom as the Settings language switcher:
  // an anchored menu that converts to a popover on wide viewports.
  const sectionAnchorRef = useRef<HTMLDivElement>(null);
  const openSectionPicker = () => {
    f7.actions
      .create({
        targetEl: sectionAnchorRef.current ?? undefined,
        buttons: [
          [
            { label: true, text: t.menu.category },
            ...categories.map((c) => ({
              text: loc(c.name),
              bold: c.id === effectiveCat,
              onClick: () => setSelectedCat(c.id),
            })),
            { text: t.cancel, color: 'red' },
          ],
        ],
      })
      .open();
  };

  const patchRow = (key: number, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, emptyRow()]);
  const removeRow = (key: number) =>
    setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.key !== key) : rs));

  // Next free sortOrder in the chosen section (products already there win the
  // lower slots), shared by both save paths.
  const baseSortFor = (catId: string) => {
    const category = categories.find((c) => c.id === catId);
    return (category?.products ?? []).reduce((m, p) => Math.max(m, p.sortOrder), 0);
  };

  const saveSimple = async () => {
    // Name is required: a row that has a price typed but no name is a mistake,
    // not a row to silently drop.
    if (rows.some((r) => r.price.trim() && !r.name.trim())) {
      f7.dialog.alert(t.menu.itemNameRequiredForPrice);
      return;
    }
    const items = rows.filter((r) => r.name.trim());
    if (items.length === 0) {
      f7.dialog.alert(t.menu.enterAtLeastOneItem);
      return;
    }
    submitting.current = true;
    const baseSort = baseSortFor(effectiveCat);
    try {
      let i = 0;
      for (const r of items) {
        i += 1;
        await createProduct.mutateAsync({
          categoryId: effectiveCat,
          body: {
            name: compactLocalized({ en: r.name.trim() })!,
            price: inputToMinor(r.price.trim() || '0', currency),
            sortOrder: baseSort + i,
          },
        });
      }
      f7.toast.show({
        text: t.menu.itemsAddedToast(items.length),
        closeTimeout: 2000,
      });
      onClose();
    } catch (e) {
      f7.dialog.alert(e instanceof Error ? e.message : t.error);
    } finally {
      submitting.current = false;
    }
  };

  // Turn a zone into a ProductOptionCreate (+ choices), or null when it has no
  // usable choice. Returns an error key instead when a choice has a price but no
  // name - same "don't silently drop" rule as the simple grid.
  const zoneToOption = (
    zone: Zone,
    isRequired: boolean,
    defaultName: Record<string, string>,
  ):
    | { error: true }
    | { error: false; option: ProductWithOptionsCreate['options'][number] }
    | null => {
    if (zone.choices.some((c) => c.price.trim() && !c.name.trim())) {
      return { error: true };
    }
    const named = zone.choices.filter((c) => c.name.trim());
    if (named.length === 0) return null;
    return {
      error: false,
      option: {
        // Blank group name → store the fully-localized default so the storefront
        // shows it in the customer's own language (not the dashboard's).
        name: zone.groupName.trim()
          ? compactLocalized({ en: zone.groupName.trim() })!
          : defaultName,
        isRequired,
        isMultiple: zone.isMultiple,
        minSelect: isRequired ? 1 : 0,
        maxSelect: zone.isMultiple ? null : 1,
        choices: named.map((c, i) => ({
          name: compactLocalized({ en: c.name.trim() })!,
          price: inputToMinor(c.price.trim() || '0', currency),
          sortOrder: i,
        })),
      },
    };
  };

  const saveAdvanced = async () => {
    if (!prodName.trim()) {
      f7.dialog.alert(t.menu.englishNameRequired);
      return;
    }
    const zones: { zone: Zone; isRequired: boolean; defaultName: Record<string, string> }[] = [
      { zone: requiredZone, isRequired: true, defaultName: REQUIRED_GROUP_NAME },
      { zone: optionalZone, isRequired: false, defaultName: OPTIONAL_GROUP_NAME },
    ];
    const options: ProductWithOptionsCreate['options'] = [];
    for (const z of zones) {
      const built = zoneToOption(z.zone, z.isRequired, z.defaultName);
      if (built?.error) {
        f7.dialog.alert(t.menu.itemNameRequiredForPrice);
        return;
      }
      if (built) options.push(built.option);
    }
    submitting.current = true;
    const baseSort = baseSortFor(effectiveCat);
    try {
      await createProductWithOptions.mutateAsync({
        categoryId: effectiveCat,
        body: {
          name: compactLocalized({ en: prodName.trim() })!,
          price: inputToMinor(prodPrice.trim() || '0', currency),
          sortOrder: baseSort + 1,
          options,
        },
      });
      f7.toast.show({ text: t.menu.itemsAddedToast(1), closeTimeout: 2000 });
      onClose();
    } catch (e) {
      f7.dialog.alert(e instanceof Error ? e.message : t.error);
    } finally {
      submitting.current = false;
    }
  };

  const save = () => {
    if (submitting.current) return;
    if (!effectiveCat) {
      f7.dialog.alert(t.menu.addCategoryFirst);
      return;
    }
    void (mode === 'advanced' ? saveAdvanced() : saveSimple());
  };

  return (
    <Popup opened={open} onPopupClosed={onClose}>
      <Page pageContent={false}>
        {/* Explicit NavTitle (not the `title` prop) so the three slots lay out as
            Cancel · title · Save and the title stays centered. */}
        <Navbar>
          <NavLeft>
            <F7Link popupClose>{t.cancel}</F7Link>
          </NavLeft>
          <NavTitle>{t.menu.newProducts}</NavTitle>
          <NavRight>
            <F7Link onClick={save}>{t.saveShort}</F7Link>
          </NavRight>
        </Navbar>
        <PageContent>
          {isLoading && (
            <Block className="text-align-center">
              <Preloader />
            </Block>
          )}

          {!isLoading && categories.length === 0 && (
            <Block>
              <p style={{ color: '#8e8e93' }}>{t.menu.addCategoryFirstThenItems}</p>
            </Block>
          )}

          {!isLoading && categories.length > 0 && (
            <>
              <Block style={{ marginBottom: 0 }}>
                <Segmented strong>
                  <Button active={mode === 'simple'} onClick={() => setMode('simple')}>
                    {t.menu.simpleMode}
                  </Button>
                  <Button active={mode === 'advanced'} onClick={() => setMode('advanced')}>
                    {t.menu.advancedMode}
                  </Button>
                </Segmented>
              </Block>

              <BlockTitle>{t.menu.category}</BlockTitle>
              <div ref={sectionAnchorRef}>
                <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
                  <ListItem
                    title={t.menu.category}
                    after={effectiveCatName ? loc(effectiveCatName) : ''}
                    link="#"
                    onClick={openSectionPicker}
                  />
                </List>
              </div>

              {mode === 'simple' ? (
                <>
                  <BlockTitle>{t.menu.items}</BlockTitle>
                  <div className="bulk-add-card">
                    {rows.map((r) => (
                      <div key={r.key} className="bulk-add-row">
                        <input
                          className="bulk-add-name"
                          type="text"
                          placeholder={t.menu.itemNamePlaceholder}
                          required
                          aria-required="true"
                          value={r.name}
                          onChange={(e) => patchRow(r.key, { name: e.target.value })}
                        />
                        <input
                          className="bulk-add-price"
                          type="number"
                          inputMode="decimal"
                          placeholder="0"
                          aria-label={t.menu.basePriceAriaLabel(currency)}
                          value={r.price}
                          onChange={(e) => patchRow(r.key, { price: e.target.value })}
                        />
                        <span className="bulk-add-currency" aria-hidden="true">
                          {currencySymbol}
                        </span>
                        <button
                          type="button"
                          className="bulk-add-remove"
                          aria-label={t.menu.removeItem}
                          disabled={rows.length === 1}
                          onClick={() => removeRow(r.key)}
                        />
                      </div>
                    ))}
                  </div>

                  <Block>
                    <div
                      className="bulk-add-addrow-btn"
                      role="button"
                      tabIndex={0}
                      onClick={addRow}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && addRow()}
                    >
                      <button
                        type="button"
                        className="bulk-add-plus"
                        aria-label={t.menu.addRow}
                        tabIndex={-1}
                      />
                      <span>{t.menu.addAnotherItem}</span>
                    </div>
                  </Block>
                </>
              ) : (
                <>
                  <BlockTitle>{t.menu.newProduct}</BlockTitle>
                  <div className="bulk-add-card">
                    <div className="bulk-add-row">
                      <input
                        className="bulk-add-name"
                        type="text"
                        placeholder={t.menu.itemNamePlaceholder}
                        required
                        aria-required="true"
                        value={prodName}
                        onChange={(e) => setProdName(e.target.value)}
                      />
                      <input
                        className="bulk-add-price"
                        type="number"
                        inputMode="decimal"
                        placeholder="0"
                        aria-label={t.menu.basePriceAriaLabel(currency)}
                        value={prodPrice}
                        onChange={(e) => setProdPrice(e.target.value)}
                      />
                      <span className="bulk-add-currency" aria-hidden="true">
                        {currencySymbol}
                      </span>
                    </div>
                  </div>

                  <AdvancedZone
                    title={t.menu.requiredGroupDefault}
                    hint={t.menu.requiredChoicesHint}
                    zone={requiredZone}
                    setZone={setRequiredZone}
                    currencySymbol={currencySymbol}
                  />
                  <AdvancedZone
                    title={t.menu.optionalGroupDefault}
                    hint={t.menu.optionalChoicesHint}
                    zone={optionalZone}
                    setZone={setOptionalZone}
                    currencySymbol={currencySymbol}
                  />
                </>
              )}
            </>
          )}
        </PageContent>
      </Page>
    </Popup>
  );
}

// One option zone in advanced mode: a group-name + option-level controls (allow
// multiple / min / max), then a list of choice rows reusing the simple grid's
// row UX (name + price delta + default toggle + remove), with a "+" add-row.
function AdvancedZone({
  title,
  hint,
  zone,
  setZone,
  currencySymbol,
}: {
  title: string;
  hint: string;
  zone: Zone;
  setZone: React.Dispatch<React.SetStateAction<Zone>>;
  currencySymbol: string;
}) {
  const t = useT();

  const patchChoice = (key: number, patch: Partial<ChoiceRow>) =>
    setZone((z) => ({
      ...z,
      choices: z.choices.map((c) => (c.key === key ? { ...c, ...patch } : c)),
    }));
  const addChoice = () => setZone((z) => ({ ...z, choices: [...z.choices, emptyChoice()] }));
  const removeChoice = (key: number) =>
    setZone((z) => ({
      ...z,
      choices: z.choices.length > 1 ? z.choices.filter((c) => c.key !== key) : z.choices,
    }));

  return (
    <>
      <BlockTitle>{title}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListInput
          label={t.menu.groupName}
          type="text"
          placeholder={hint}
          value={zone.groupName}
          onInput={(e) =>
            setZone((z) => ({ ...z, groupName: (e.target as HTMLInputElement).value }))
          }
        />
      </List>
      <div className="bulk-add-card" style={{ marginTop: -8 }}>
        <div
          className="bulk-add-row"
          role="button"
          tabIndex={0}
          onClick={(e) => {
            f7.actions
              .create({
                targetEl: e.currentTarget as HTMLElement,
                convertToPopover: true,
                buttons: [
                  [
                    {
                      text: t.menu.pickOneMode,

                      onClick: () => setZone((z) => ({ ...z, isMultiple: false })),
                    },
                    {
                      text: t.menu.pickManyMode,

                      onClick: () => setZone((z) => ({ ...z, isMultiple: true })),
                    },
                  ],
                ],
              })
              .open();
          }}
        >
          <span className="bulk-add-label">{t.menu.selectionMode}</span>
          <span style={{ color: 'var(--f7-theme-color)' }}>
            {zone.isMultiple ? t.menu.pickManyMode : t.menu.pickOneMode}
          </span>
        </div>
      </div>

      <div className="bulk-add-card">
        {zone.choices.map((c) => (
          <div key={c.key} className="bulk-add-row">
            <input
              className="bulk-add-name"
              type="text"
              placeholder={t.menu.choiceNamePlaceholder}
              value={c.name}
              onChange={(e) => patchChoice(c.key, { name: e.target.value })}
            />
            <input
              className="bulk-add-price"
              type="number"
              inputMode="decimal"
              placeholder="0"
              aria-label={t.menu.extraPrice}
              value={c.price}
              onChange={(e) => patchChoice(c.key, { price: e.target.value })}
            />
            <span className="bulk-add-currency" aria-hidden="true">
              {currencySymbol}
            </span>
            <button
              type="button"
              className="bulk-add-remove"
              aria-label={t.menu.removeItem}
              disabled={zone.choices.length === 1}
              onClick={() => removeChoice(c.key)}
            />
          </div>
        ))}
      </div>

      <Block>
        <div
          className="bulk-add-addrow-btn"
          role="button"
          tabIndex={0}
          onClick={addChoice}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && addChoice()}
        >
          <button
            type="button"
            className="bulk-add-plus"
            aria-label={t.menu.addRow}
            tabIndex={-1}
          />
          <span>{t.menu.addChoice}</span>
        </div>
      </Block>
    </>
  );
}
